import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { WebActivityLinkedService } from 'app/model/linkedservice/web-activity.linkedservice';
import { ValueAdapter } from '../value.adapter';
import { ReferenceAdapter } from 'app/model/adapters/reference.adapter';
import { AuthenticationType, ConnectionAuthKind, Credential, ITridentResource, restAuthTypes } from 'shell-models-lib';
import {
    parameterizableSecretBaseToModel,
    parameterizablePasswordOrAKVtoSecretBase
} from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { FabricDataPipelinesLinkedService } from 'app/model/linkedservice/fabric-data-pipelines.linkedservice';
import { MicrosoftOutlookLinkedService } from 'app/model/linkedservice/microsoftoutlook.linkedservice';
import { PowerBILinkedService } from 'app/model/linkedservice/powerbi.linkedservice';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(WebActivityLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                authenticationType: '*',
                url: '*',
                userName: '*',
                aadResourceId: '*',
                tenant: '*',
                password: 'secret',
                pfx: 'secret',
                credential: '*',
                enableServerCertificateValidation: '*',
                servicePrincipalId: '*',
                servicePrincipalKey: 'secret'
            },
            parameters: '*'
        }
    }
})

export class WebActivityLinkedServiceAdapter extends LinkedServiceAdapter<WebActivityLinkedService> {

    protected modelConstructor(): new () => WebActivityLinkedService {
        return WebActivityLinkedService;
    }

    protected model(): IModel {
        return WebActivityLinkedService.MODEL;
    }

    protected override toAzureTypeProperties = (tridentResource: ITridentResource): Partial<Model.WebActivityLinkedServiceTypeProperties> => {
        const datasourceReference: Record<'kind' | 'path', string> = JSON.parse(tridentResource.datasourceReference);
        const pathProperties = datasourceReference.path.split(';');
        const credentialType = tridentResource.credentialDetails.credentialType;
        const typeProperties: Model.WebActivityLinkedServiceTypeProperties = {
            url: pathProperties[0],
            aadResourceId: pathProperties[1] ?? undefined,
            enableServerCertificateValidation: false  //connection dont have this value, we are not using it anywhere in the ux code
        };
        switch (credentialType) {
            case ConnectionAuthKind.ServicePrincipal:
                typeProperties.authenticationType = restAuthTypes.AadServicePrincipal;
                break;
            case ConnectionAuthKind.Basic:
                typeProperties.authenticationType = AuthenticationType.Basic;
                break;
            case ConnectionAuthKind.OAuth2:
                typeProperties.authenticationType = AuthenticationType.UserAuth;
                break;
            default:
        }
        return typeProperties;
    };

    protected async propertiesToResourceAsync(linkedService: WebActivityLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.WebActivityLinkedService;
        properties.type = this._getLinkedServiceType(linkedService);
        properties.typeProperties = {
            url: this.valueToResource(linkedService, WebActivityLinkedService.URL),
            enableServerCertificateValidation: linkedService.enableServerCertificateValidation
        };
        if (linkedService.authentication && linkedService.authentication !== 'None') {
            // irrespective of auth type, if auth ref is credential, populate cred and resource instead of type
            if (linkedService.authentication === 'UserAssignedManagedIdentity' || linkedService.authentication === 'UserAuth') {
                properties.typeProperties.aadResourceId = this.valueToResource(linkedService, WebActivityLinkedService.RESOURCE);
                if (!__IS_FABRIC_DPE__) {
                    if (linkedService.credential) {
                        properties.typeProperties.credential = ReferenceAdapter.toResource(linkedService.credential, this.context) as Model.CredentialReference;
                    }
                } else {
                    properties.typeProperties.authenticationType = linkedService.authentication;
                }
            }
            properties.typeProperties.authenticationType = linkedService.authentication;

            // inline options
            if (linkedService.authentication === 'Basic') {
                properties.typeProperties.userName = this.valueToResource(linkedService, WebActivityLinkedService.USERNAME);
                properties.typeProperties.password = parameterizablePasswordOrAKVtoSecretBase(linkedService, WebActivityLinkedService.PASSWORD, this.context);
            } else if (linkedService.authentication === 'ClientCertificate' && linkedService.pfx) {
                properties.typeProperties.pfx =  parameterizablePasswordOrAKVtoSecretBase(linkedService, WebActivityLinkedService.PFX, this.context);
                properties.typeProperties.password = parameterizablePasswordOrAKVtoSecretBase(linkedService, WebActivityLinkedService.PASSWORD, this.context);
            } else if (linkedService.authentication === 'ManagedServiceIdentity') {
                properties.typeProperties.aadResourceId = this.valueToResource(linkedService, WebActivityLinkedService.RESOURCE);
            } else if (linkedService.authentication === 'AadServicePrincipal') {
                if (linkedService.pfx) {
                    properties.typeProperties.pfx =  parameterizablePasswordOrAKVtoSecretBase(linkedService, WebActivityLinkedService.PFX, this.context);
                }
                if (linkedService.servicePrincipalKey) {
                    properties.typeProperties.servicePrincipalKey = parameterizablePasswordOrAKVtoSecretBase(linkedService, WebActivityLinkedService.SERVICE_PRINCIPAL_KEY, this.context);
                }
                if (linkedService.credential) {
                    properties.typeProperties.credential = ReferenceAdapter.toResource(linkedService.credential, this.context) as Model.CredentialReference;
                } else {
                    properties.typeProperties.tenant = this.valueToResource(linkedService, WebActivityLinkedService.TENANT);
                    properties.typeProperties.servicePrincipalId = this.valueToResource(linkedService, WebActivityLinkedService.SERVICE_PRINCIPAL_ID);
                }
                properties.typeProperties.aadResourceId = this.valueToResource(linkedService, WebActivityLinkedService.RESOURCE);
            }
        }

        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: WebActivityLinkedService): Promise<WebActivityLinkedService> {
        const properties = linkedServiceResource.properties as Model.WebActivityLinkedService;
        const typeProperties: Model.WebActivityLinkedServiceTypeProperties = properties.typeProperties;

        ValueAdapter.toModel(linkedService, WebActivityLinkedService.URL, typeProperties.url);

        if (typeProperties.authenticationType) {
            if (typeProperties.credential) {
                linkedService.credential = ReferenceAdapter.toProxyModel(Metamodel.modelFor(Credential), typeProperties.credential, this.context);
            }

            switch (typeProperties.authenticationType) {
                case 'Basic':
                    linkedService.authentication = 'Basic';
                    ValueAdapter.toModel(linkedService, WebActivityLinkedService.USERNAME, typeProperties.userName);
                    parameterizableSecretBaseToModel(linkedService, WebActivityLinkedService.PASSWORD, typeProperties.password, this.context);
                    break;
                case 'ClientCertificate':
                    linkedService.authentication = 'ClientCertificate';
                    parameterizableSecretBaseToModel(linkedService, WebActivityLinkedService.PFX, typeProperties.pfx, this.context);
                    parameterizableSecretBaseToModel(linkedService, WebActivityLinkedService.PASSWORD, typeProperties.password, this.context);
                    break;
                case 'ManagedServiceIdentity':
                    linkedService.authentication = 'ManagedServiceIdentity';
                    ValueAdapter.toModel(linkedService, WebActivityLinkedService.RESOURCE, typeProperties.aadResourceId);
                    break;
                case 'AadServicePrincipal':
                    linkedService.authentication = 'AadServicePrincipal';

                    ValueAdapter.toModel(linkedService, WebActivityLinkedService.TENANT, typeProperties.tenant);
                    ValueAdapter.toModel(linkedService, WebActivityLinkedService.SERVICE_PRINCIPAL_ID, typeProperties.servicePrincipalId);
                    ValueAdapter.toModel(linkedService, WebActivityLinkedService.RESOURCE, typeProperties.aadResourceId);

                    if (typeProperties.pfx) {
                        parameterizableSecretBaseToModel(linkedService, WebActivityLinkedService.PFX, typeProperties.pfx, this.context);
                        linkedService.isSpnAuthKey = false;
                    } else if (typeProperties.servicePrincipalKey) {
                        parameterizableSecretBaseToModel(linkedService, WebActivityLinkedService.SERVICE_PRINCIPAL_KEY, typeProperties.servicePrincipalKey, this.context);
                        linkedService.isSpnAuthKey = true;
                    } else if (typeProperties.credential) {
                        linkedService.isSpnAuthRefMethodCredential = typeProperties.credential === undefined ? false : true;
                    }
                    break;
                case 'UserAssignedManagedIdentity':
                    linkedService.authentication = 'UserAssignedManagedIdentity';
                    ValueAdapter.toModel(linkedService, WebActivityLinkedService.RESOURCE, typeProperties.aadResourceId);
                    break;
                case 'UserAuth':
                    linkedService.authentication = 'UserAuth';
                    ValueAdapter.toModel(linkedService, WebActivityLinkedService.RESOURCE, typeProperties.aadResourceId);
                    break;
                default:
                    break;
            }
        }
        linkedService.enableServerCertificateValidation = typeProperties.enableServerCertificateValidation;

        return linkedService;
    }

    private _getLinkedServiceType(linkedService: WebActivityLinkedService): string {
        if (linkedService instanceof PowerBILinkedService) {
            return  Metamodel.modelFor(PowerBILinkedService).name;
        } else if (linkedService instanceof FabricDataPipelinesLinkedService) {
            return Metamodel.modelFor(FabricDataPipelinesLinkedService).name;
        } else if (linkedService instanceof MicrosoftOutlookLinkedService) {
            return Metamodel.modelFor(MicrosoftOutlookLinkedService).name;
        }
        return Metamodel.modelFor(WebActivityLinkedService).name;
    }
}
